import type { PlayerSearchResult, TeamSearchResult } from '@hooks/useQuickStats';
import type { LeagueKey } from '@parlay-picker/shared/league';
import { getTeamLogo } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { type ReactElement, useState } from 'react';

// =============================================================================
// PlayerResultRow
// =============================================================================

export function PlayerResultRow({
  player,
  leagueKey,
}: {
  player: PlayerSearchResult;
  leagueKey: LeagueKey;
}): ReactElement {
  const [imageError, setImageError] = useState(false);
  const headshotUrl = usePlayerHeadshotUrl(player.name, leagueKey);
  const teamLogo = player.team ? getTeamLogo(player.team, leagueKey) : null;

  return (
    <>
      <div className="relative shrink-0">
        {headshotUrl && !imageError ? (
          <img
            src={headshotUrl}
            alt={player.name}
            className="w-10 h-10 rounded-full object-cover bg-theme-surface-hover"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center text-sm font-medium text-theme-text-secondary">
            {player.name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)}
          </div>
        )}
        {teamLogo && (
          <img
            src={teamLogo}
            alt=""
            className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-theme-surface border border-theme-border"
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{player.name}</div>
        <div className="text-sm text-theme-text-secondary">
          {player.teamAbbr || '???'} · {player.position || '?'}
        </div>
      </div>
      <div className="text-xs text-theme-text-tertiary bg-theme-surface-hover/50 px-2 py-1 rounded">
        Player
      </div>
    </>
  );
}

// =============================================================================
// TeamResultRow
// =============================================================================

export function TeamResultRow({
  team,
  leagueKey,
}: {
  team: TeamSearchResult;
  leagueKey: LeagueKey;
}): ReactElement {
  const logo = getTeamLogo(team.name, leagueKey);

  return (
    <>
      <div className="shrink-0">
        {logo ? (
          <img
            src={logo}
            alt={team.name}
            loading="lazy"
            decoding="async"
            className="w-10 h-10 object-contain"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-theme-surface-hover flex items-center justify-center text-sm font-bold text-theme-text-secondary">
            {team.abbr}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-white font-medium truncate">{team.name}</div>
        <div className="text-sm text-theme-text-secondary">{team.abbr}</div>
      </div>
      <div className="text-xs text-theme-text-tertiary bg-theme-surface-hover/50 px-2 py-1 rounded">
        Team
      </div>
    </>
  );
}
